import type {
  IngridCompleteSessionResponse,
  Option,
  OrderDTO,
  PaymentProviderConfig,
  ShippingProviderConfig,
  ShippingProviderName,
  SveaOrder
} from 'ecosystem'
import { getStorefrontOrderByOrderNumber } from '../getStorefrontOrderByOrderNumber'
import { sveaApi } from '../../../utils/svea'
import { createError } from '../../../utils/fetching'
import { alertService } from '../../../utils/alert-service'
import { completeStorefrontOrder } from './completeStorfrontOrder'
import type { OrderProvisionResult, ProvisionOrderOptions } from './types'
import {
  generateIngridShippingProviderResult,
  generateNshiftShippingProviderResult,
  generatePostnordShippingProviderResult,
  generateSveaAddresses,
  isOrderAlreadyProvisioned,
  isOrderFresh
} from './utils'

export interface ProvisionOrderSveaProviderResult {
  sveaResponse: SveaOrder
  storefrontOrder: Option<OrderDTO>
  shippingProviderResult?: {
    providerName: ShippingProviderName
    ingridResponse?: IngridCompleteSessionResponse['session']
  }
}

export const provisionOrderSveaProvider = async (
  orderId: string,
  paymentProviderConfig?: PaymentProviderConfig,
  shippingProviderConfig?: ShippingProviderConfig,
  options?: ProvisionOrderOptions
): Promise<Option<ProvisionOrderSveaProviderResult>> => {
  // Resolve {SF order, Svea OrderId} from whichever identifier the caller has.
  //
  // Push handler (default, identifierKind='paymentId'): Svea's PushUri template gives us the Svea
  // OrderId, and we look up the SF order via Svea's ClientOrderNumber below.
  //
  // Confirmation page (identifierKind='orderNumber'): Svea's ConfirmationUri does NOT support a
  // template, so we hardcoded the SF orderNumber when creating the Svea order. Look up the SF
  // order first and read the Svea OrderId from its persisted paymentId (set at order creation
  // time in createOrderHandler/sveaPaymentProvider.ts).
  const preResolvedOrder: OrderDTO | null = await (async (): Promise<OrderDTO | null> => {
    if (options?.identifierKind !== 'orderNumber') {
      return null
    }

    const { error: getOrderError, data } = await getStorefrontOrderByOrderNumber({
      orderNumber: orderId
    })

    if (getOrderError || !data) {
      void alertService.send('Provision order | Get storefront order', {
        blocks: [
          { title: 'Payload', content: { orderNumber: orderId } },
          { title: 'Error', content: getOrderError }
        ]
      })

      throw createError(getOrderError ?? 'BE GET endpoint error')
    }

    if (!data.paymentId) {
      void alertService.send('Provision order | SF order missing Svea paymentId', {
        blocks: [
          { title: 'Payload', content: { orderNumber: orderId } },
          { title: 'Storefront Order', content: data }
        ]
      })

      throw createError('SF order is missing Svea paymentId — cannot resolve Svea order')
    }

    return data
  })()

  const sveaOrderId = preResolvedOrder?.paymentId ?? orderId

  // 1. Confirmation in Svea
  const { error: sveaError, data: sveaOrderResponse } =
    await sveaApi.checkout.retrieveOrder(sveaOrderId)

  if (sveaError || !sveaOrderResponse) {
    void alertService.send('Provision order | Get svea order', {
      blocks: [
        {
          title: 'Payload',
          content: {
            orderId: sveaOrderId
          }
        },
        {
          title: 'Error',
          content: sveaError
        }
      ]
    })

    throw Error(sveaError || 'Svea GET endpoint error')
  }

  if (sveaOrderResponse.Status !== 'Final') {
    void alertService.send('Provision order | Svea order not finalized', {
      blocks: [
        {
          title: 'Payload',
          content: {
            orderId: sveaOrderId
          }
        },
        {
          title: 'Svea Order Response',
          content: sveaOrderResponse
        },
        {
          title: 'Error',
          content: `Svea order status is ${sveaOrderResponse.Status}, expected 'Final'`
        }
      ]
    })

    throw Error('Svea order is not finalized yet')
  }

  // 2. Resolve the SF order from Svea's ClientOrderNumber when we didn't already (push path).
  const orderData: OrderDTO = await (async (): Promise<OrderDTO> => {
    if (preResolvedOrder) {
      return preResolvedOrder
    }

    const { error: getOrderError, data } = await getStorefrontOrderByOrderNumber({
      orderNumber: sveaOrderResponse.ClientOrderNumber
    })

    // 3. If the order doesn't exist, returns an error
    if (getOrderError || !data) {
      void alertService.send('Provision order | Get storefront order', {
        blocks: [
          {
            title: 'Payload',
            content: {
              orderNumber: sveaOrderResponse.ClientOrderNumber
            }
          },
          {
            title: 'Svea Order Response',
            content: sveaOrderResponse
          },
          {
            title: 'Error',
            content: getOrderError
          }
        ]
      })

      throw createError(getOrderError ?? 'BE GET endpoint error')
    }

    return data
  })()

  // Idempotency guard: the order is provisioned exactly once. If it is no longer CREATED, another
  // path (confirmation page, or an earlier push retry) already provisioned it — return the existing
  // order without re-booking shipment or re-writing it, to avoid duplicates.
  if (isOrderAlreadyProvisioned(orderData)) {
    return { storefrontOrder: orderData, sveaResponse: sveaOrderResponse }
  }

  // Fallback gate (payment push only): a still-fresh order belongs to the confirmation page; throw
  // so the push endpoint responds non-2xx and the provider retries later. This keeps the page the
  // single provisioner during the post-checkout window, eliminating the page-vs-push race.
  if (options?.asFallback && isOrderFresh(orderData)) {
    throw createError(
      'Payment push received for a still-fresh order; deferring to the confirmation page'
    )
  }

  const getShippingProviderResult = async (): Promise<
    OrderProvisionResult['shippingProviderResult'] | void
  > => {
    if (!orderData?.shippingProvider) {
      return
    }

    switch (orderData?.shippingProvider) {
      case 'INGRID':
        return generateIngridShippingProviderResult(orderData)
      case 'POSTNORD': {
        const { customerInfo } = generateSveaAddresses(sveaOrderResponse)

        return generatePostnordShippingProviderResult(
          orderData,
          {
            phoneNo: customerInfo.phone,
            smsNo: customerInfo.phone,
            emailAddress: customerInfo.email,
            contactName: customerInfo.fullName
          },
          shippingProviderConfig
        )
      }
      case 'NSHIFT': {
        const { customerInfo } = generateSveaAddresses(sveaOrderResponse)

        return generateNshiftShippingProviderResult(orderData, {
          phone: customerInfo.phone,
          email: customerInfo.email,
          name: customerInfo.fullName
        })
      }
      case 'DEFAULT':
    }
  }

  // 4. Get shipping provider information
  const shippingProviderResult = await getShippingProviderResult()

  // 5. Complete storefront order, change status to PAID and update all info
  const storefrontOrder = await completeStorefrontOrder({
    order: orderData,
    paymentProviderInfo: {
      ...paymentProviderConfig,
      providerName: 'SVEA',
      providerResponse: {
        sveaResponse: sveaOrderResponse
      }
    },
    ...(shippingProviderResult && {
      shippingProviderInfo: shippingProviderResult
    })
  })

  return {
    storefrontOrder,
    sveaResponse: sveaOrderResponse,
    ...(shippingProviderResult && {
      shippingProviderResult
    })
  }
}
